import {
  Cliente,
  ClienteCreateDTO,
  ClienteUpdateDTO,
  ConfigCreditoVendedorDTO,
  CreditoAbonoDTO,
  CreditoCreateDTO,
  CreditoItem,
  MovimientoCredito,
  MovimientoCreateDTO,
  HistorialRow,
  ResumenCreditoLocal,
} from '../types/clientes.types';
import { fetchAPI } from './apiClient';

/**
 * Mapea respuesta del backend (snake_case) a aliases camelCase
 * para compatibilidad con componentes existentes.
 */
function mapCliente(raw: any): Cliente {
  return {
    ...raw,
    // Asegurar que valores monetarios sean números
    saldo_pendiente: Number(raw.saldo_pendiente) || 0,
    limite_credito: Number(raw.limite_credito) || 0,
    // Aliases camelCase
    limiteCredito: Number(raw.limite_credito) || 0,
    saldoPendiente: Number(raw.saldo_pendiente) || 0,
    localId: raw.local_id,
    estado: raw.bloqueado_credito ? 'SUSPENDIDO' : (raw.activo ? 'ACTIVO' : 'INACTIVO'),
    fechaRegistro: raw.created_at ? new Date(raw.created_at) : new Date(),
    vendedor_id: raw.vendedor_id,
    creditos: (raw.creditos || []).map(mapCredito),
  };
}

function mapCredito(raw: any): CreditoItem {
  return {
    ...raw,
    monto_total: Number(raw.monto_total) || 0,
    saldo_actual: Number(raw.saldo_actual) || 0,
  };
}

function mapResumenCreditoLocal(raw: any): ResumenCreditoLocal {
  return {
    local_id: raw.local_id,
    total_credito_local: Number(raw.total_credito_local) || 0,
    vendedores: (raw.vendedores || []).map((item: any) => ({
      ...item,
      saldo_total_activo: Number(item.saldo_total_activo) || 0,
      max_monto_total: Number(item.max_monto_total) || 0,
    })),
  };
}

class CreditoService {
  // ─── CLIENTES ────────────────────────────

  async listarClientes(localId?: string): Promise<Cliente[]> {
    const params = new URLSearchParams();
    if (localId) params.append('local_id', localId);
    const raw = await fetchAPI<any[]>(`/clientes?${params.toString()}`);
    return raw.map(mapCliente);
  }

  async obtenerCliente(id: string): Promise<Cliente> {
    const raw = await fetchAPI<any>(`/clientes/${id}`);
    return mapCliente(raw);
  }

  async crearCliente(data: ClienteCreateDTO): Promise<Cliente> {
    const raw = await fetchAPI<any>('/clientes', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return mapCliente(raw);
  }

  async actualizarCliente(id: string, data: ClienteUpdateDTO): Promise<Cliente> {
    const raw = await fetchAPI<any>(`/clientes/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    return mapCliente(raw);
  }

  async toggleBloqueo(id: string, bloqueadoActual: boolean): Promise<Cliente> {
    return this.actualizarCliente(id, { bloqueado_credito: !bloqueadoActual });
  }

  async actualizarLimite(id: string, limite: number): Promise<Cliente> {
    return this.actualizarCliente(id, { limite_credito: limite });
  }

  async desactivarCliente(id: string): Promise<Cliente> {
    return this.actualizarCliente(id, { activo: false });
  }

  // ─── HISTORIAL ───────────────────────────

  async obtenerHistorial(clienteId: string): Promise<HistorialRow[]> {
    // Backend devuelve movimientos dentro de GET /clientes/{id}
    const raw = await fetchAPI<any>(`/clientes/${clienteId}`);
    const movimientos: any[] = raw.movimientos || [];
    // Ordenar cronológicamente y calcular saldo acumulado
    let saldoAcumulado = 0;
    return movimientos
      .sort((a: any, b: any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
      .map((m: any) => {
        const monto = Number(m.monto) || 0;
        // SUMA/POSITIVO = cargo (increases saldo/debt), RESTA/NEGATIVO = abono (decreases)
        const esCargo = m.efecto === 'SUMA' || m.efecto === 'POSITIVO';
        saldoAcumulado += esCargo ? monto : -monto;
        return {
          id: m.id,
          fecha: m.created_at,
          tipo: m.tipo,
          descripcion: m.descripcion || '',
          cargo: esCargo ? monto : null,
          abono: !esCargo ? monto : null,
          saldo_acumulado: saldoAcumulado,
          referencia_tipo: m.referencia_tipo || null,
        };
      });
  }

  // ─── MOVIMIENTOS ─────────────────────────

  async registrarMovimiento(
    clienteId: string,
    data: MovimientoCreateDTO
  ): Promise<MovimientoCredito> {
    return fetchAPI<MovimientoCredito>(`/clientes/${clienteId}/movimiento`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async crearCredito(data: CreditoCreateDTO): Promise<CreditoItem> {
    const raw = await fetchAPI<any>('/creditos', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return mapCredito(raw);
  }

  async abonarCredito(creditoId: string, data: CreditoAbonoDTO): Promise<any> {
    return fetchAPI<any>(`/creditos/${creditoId}/abono`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async obtenerResumenLocal(): Promise<ResumenCreditoLocal> {
    const raw = await fetchAPI<any>('/creditos/resumen-local');
    return mapResumenCreditoLocal(raw);
  }

  async guardarConfigVendedor(vendedorId: string, data: ConfigCreditoVendedorDTO): Promise<any> {
    return fetchAPI<any>(`/vendedor/${vendedorId}/credito-config`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
}

export const creditoService = new CreditoService();
export default creditoService;
